import { Group, Text } from '@mantine/core';
import dayjs from 'dayjs';
import { PiClockCounterClockwise } from 'react-icons/pi';
import DateSinceDisplay from '@/components/common/DateSinceDisplay';
import { TLastRecordedChange } from '@/models/invoicingCompanies';

const STALE_AFTER_MONTHS = 12;

const OPERATION_VERBS: Record<TLastRecordedChange['operation'], string> = {
  created: 'added',
  updated: 'changed',
  removed: 'removed',
};

/**
 * Answers "how old are these details?" at a glance. Deliberately says
 * "recorded": edits made by migration, script or psql leave no record, so the
 * page must not claim to be the unconditional last modification.
 */
export default function LastRecordedChange({
  change,
}: {
  change: TLastRecordedChange | null;
}) {
  if (!change) {
    return (
      <Text size="sm" c="dimmed">
        No detail changes have been recorded.
      </Text>
    );
  }

  const stale = dayjs().diff(dayjs(change.at), 'month') >= STALE_AFTER_MONTHS;
  const what = change.label
    ? `${change.entityLabel} - ${change.label}`
    : change.entityLabel;

  return (
    <Group gap={6} c={stale ? 'orange.8' : 'dimmed'}>
      <PiClockCounterClockwise size={15} />
      <Text size="sm" c="inherit">
        {what} {OPERATION_VERBS[change.operation]}{' '}
        <DateSinceDisplay date={change.at} size="sm" c="inherit" fw={500} />
        {change.actor ? ` by ${change.actor}` : ''}
      </Text>
    </Group>
  );
}
